import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { SidebarComponent } from '../../../sidebar.component';
import {
  SidebarListsContract,
  SidebarTaskGroupUi,
} from '../../../model/sidebar.model';
import {
  LimitedResultOfTaskGroupDto,
  MeClient,
} from '@r1-development/resolution-mechanicalturk-api-client';
import { SidebarApiService } from '../../../services/sidebar-api.service';
import {
  GetTaskGroupsParams,
  TaskTypes,
} from 'src/app/shared/models/tasks.model';
import { LazyLoadContainerComponent } from '../../lazy-load-container/lazy-load-container.component';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { SidebarGroupTasksComponent } from '../../sidebar-group-tasks/sidebar-group-tasks.component';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TaskGroupsClientMemoizedService } from 'src/app/shared/services/memoized-apis/task-groups-client.memoized.service';
import { take } from 'rxjs';

@Component({
  selector: 'ri-resolution-ui-my-open-tasks',
  imports: [
    SidebarGroupTasksComponent,
    LazyLoadContainerComponent,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatIcon,
    MatButtonModule,
  ],
  templateUrl: './my-open-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyOpenTasksComponent implements SidebarListsContract<GetTaskGroupsParams> {
  private readonly sidebarApiService = inject(SidebarApiService);
  private readonly meClient = inject(MeClient);
  private readonly snackBar = inject(MatSnackBar);

  private readonly sidebarComponentComponent = inject<SidebarComponent>(
    forwardRef(() => SidebarComponent)
  );
  private readonly taskGroupsClientMemoizedService = inject(
    TaskGroupsClientMemoizedService
  );

  public readonly allowedTaskTypes =
    this.sidebarComponentComponent.allowedTaskTypes;

  readonly form = this.sidebarComponentComponent.form;

  private taskGroupsListGenerator?: AsyncGenerator<LimitedResultOfTaskGroupDto>;

  private readonly _groups = signal<SidebarTaskGroupUi[]>([]);
  public readonly groups = this._groups.asReadonly();

  private readonly _hasGroupsToFetch = signal<boolean>(false);
  public readonly hasGroupsToFetch = this._hasGroupsToFetch.asReadonly();

  private readonly _isLoading = signal<boolean>(false);
  public readonly isLoading = this._isLoading.asReadonly();

  public readonly hasNoTasks = computed<boolean>(() => {
    const groups = this.groups();
    return groups.length === 0;
  });

  public readonly hasTasksLessThanLimit = computed<boolean>(() => {
    const groups = this.groups();
    return (
      groups.length > 0 && groups.length < SidebarApiService.LOAD_PAGE_SIZE
    );
  });

  public readonly autoAssignEnabled = computed<boolean>(() => {
    return this.groups().length <= 2;
  });

  public readonly taskTypes =
    this.taskGroupsClientMemoizedService.getAllTaskTypesUi();

  reloadData(params: GetTaskGroupsParams): void {
    this._groups.set([]);
    this._hasGroupsToFetch.set(false);

    if (params.assigned === false) {
      this.taskGroupsListGenerator =
        this.sidebarApiService.createAvailableTasksListGenerator({
          searchTerm: params.searchTerm,
          taskTypes: params.taskTypes,
        });
      this.loadTaskGroups({ reload: true });
    } else {
      this.taskGroupsListGenerator =
        this.sidebarApiService.createMyOpenTasksListGenerator({
          ...params,
        });

      if (params.taskTypes && params.taskTypes.length > 0) {
        this.loadTaskGroups({ reload: true });
      }
    }
  }

  onAutoAssignTasks(): void {
    this.meClient
      .allocateTaskGroups({ bypassNoAssignedPrerequisite: true })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.failedPrerequisites?.length) {
            this.snackBar.open(
              `Auto assign failed: ${response.failedPrerequisites.join(', ')}`,
              'close',
              { duration: 5000, panelClass: ['error'] }
            );
          } else {
            this.snackBar.open('Tasks auto assigned successfully.', 'close', {
              duration: 5000,
              panelClass: ['success'],
            });
            this.sidebarApiService.clearCache();
            this.sidebarComponentComponent.reloadData();
          }
        },
        error: () => {
          this.snackBar.open('Failed to auto assign tasks.', 'close', {
            duration: 5000,
            panelClass: ['error'],
          });
        },
      });
  }

  async loadTaskGroups(
    options: { reload: boolean } = { reload: false }
  ): Promise<void> {
    this._isLoading.set(true);

    try {
      const result = await SidebarComponent.loadTaskGroups(
        this.taskGroupsListGenerator!
      );

      if (result.done) {
        return;
      }

      if (options.reload) {
        this._groups.set(result.groups);
      } else {
        this._groups.update((oldGroups) => [...oldGroups, ...result.groups]);
      }

      this._hasGroupsToFetch.set(result.hasMore);
    } finally {
      this._isLoading.set(false);
    }
  }
}
